/**
 * 📢 BROADCAST SERVICE
 * Manage message templates and campaigns
 */

import { getApiBaseUrl } from '@/config/environment';


export type TemplateType = 'sms' | 'email' | 'both';
export type TemplateCategory = 'marketing' | 'transactional' | 'notification' | 'promotional';
export type CampaignType = 'sms' | 'email' | 'both';
export type TargetAudience = 'all_users' | 'specific_users' | 'user_segment' | 'all_hosts' | 'all_drivers' | 'all_renters';

export interface MessageTemplate {
  id: string;
  name: string;
  templateType: TemplateType;
  category: TemplateCategory;
  smsMessage?: string;
  emailSubject?: string;
  emailBodyHtml?: string;
  variables: string[];
  usageCount: number;
  isActive: boolean;
  createdBy: {
    id: string;
    email: string;
    fullName: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateTemplateData {
  name: string;
  templateType: TemplateType;
  category: TemplateCategory;
  smsMessage?: string;
  emailSubject?: string;
  emailBodyHtml?: string;
  variables: string[];
  isActive?: boolean;
}

export interface UpdateTemplateData {
  name?: string;
  templateType?: TemplateType;
  category?: TemplateCategory;
  smsMessage?: string;
  emailSubject?: string;
  emailBodyHtml?: string;
  variables?: string[];
  isActive?: boolean;
}

export interface BroadcastCampaign {
  id: string;
  name: string;
  campaignType: CampaignType;
  templateId?: string;
  template?: {
    id: string;
    name: string;
  };
  customMessage?: {
    smsMessage?: string;
    emailSubject?: string;
    emailBodyHtml?: string;
  };
  targetAudience: TargetAudience;
  targetUserIds?: string[];
  segmentFilters?: {
    city?: string;
    status?: string;
    userType?: string;
  };
  templateVariables?: Record<string, any>;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  openedCount?: number;
  clickedCount?: number;
  scheduledAt?: string;
  sentAt?: string;
  completedAt?: string;
  createdBy: {
    id: string;
    email: string;
    fullName: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface SendCampaignData {
  name: string;
  campaignType: CampaignType;
  templateId?: string;
  customMessage?: {
    smsMessage?: string;
    emailSubject?: string;
    emailBodyHtml?: string;
  };
  targetAudience: TargetAudience;
  targetUserIds?: string[]; // UUIDs for internal user IDs
  targetPhones?: string[]; // Phone numbers for specific user targeting
  targetEmails?: string[]; // Email addresses for specific user targeting
  segmentFilters?: {
    city?: string;
    status?: string;
    userType?: string;
  };
  templateVariables?: Record<string, any>;
  scheduledAt?: string;
}

export interface CampaignStats {
  campaignId: string;
  campaignName: string;
  campaignType: CampaignType;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  openedCount: number;
  clickedCount: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  recipients: {
    userId: string;
    userName: string;
    userEmail: string;
    userPhone?: string;
    smsStatus?: 'sent' | 'delivered' | 'failed';
    emailStatus?: 'sent' | 'delivered' | 'opened' | 'clicked' | 'failed';
    sentAt: string;
    deliveredAt?: string;
    openedAt?: string;
    clickedAt?: string;
    failureReason?: string;
  }[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export const broadcastService = {
  // ========== TEMPLATES ==========

  /**
   * Get all templates
   */
  async getTemplates(filters?: {
    category?: TemplateCategory;
    templateType?: TemplateType;
    isActive?: boolean;
  }): Promise<ApiResponse<{ templates: MessageTemplate[]; total: number }>> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.templateType) queryParams.append('templateType', filters.templateType);
      if (filters?.isActive !== undefined) queryParams.append('isActive', String(filters.isActive));

      const url = `/api/v1/admin/broadcasts/templates${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(`${getApiBaseUrl()}${url}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch templates'
      };
    }
  },

  /**
   * Get single template by ID
   */
  async getTemplate(templateId: string): Promise<ApiResponse<MessageTemplate>> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/broadcasts/templates/${templateId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching template:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch template'
      };
    }
  },

  /**
   * Create new template
   */
  async createTemplate(data: CreateTemplateData): Promise<ApiResponse<MessageTemplate>> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/broadcasts/templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Backend error response:', errorData);

        // Handle different error formats
        const errorMessage = typeof errorData.error === 'string'
          ? errorData.error
          : errorData.error?.message || errorData.message || JSON.stringify(errorData.error) || `API Error ${response.status}`;

        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error creating template:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create template'
      };
    }
  },

  /**
   * Update template
   */
  async updateTemplate(templateId: string, data: UpdateTemplateData): Promise<ApiResponse<MessageTemplate>> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/broadcasts/templates/${templateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Backend error response:', errorData);

        const errorMessage = typeof errorData.error === 'string'
          ? errorData.error
          : errorData.error?.message || errorData.message || JSON.stringify(errorData.error) || `API Error ${response.status}`;

        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error updating template:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update template'
      };
    }
  },

  /**
   * Delete template
   */
  async deleteTemplate(templateId: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/broadcasts/templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API Error ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error deleting template:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete template'
      };
    }
  },

  // ========== CAMPAIGNS ==========

  /**
   * Send campaign
   */
  async sendCampaign(data: SendCampaignData): Promise<ApiResponse<BroadcastCampaign>> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/broadcasts/campaigns/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });


      if (!response.ok) {
        let errorData;
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          const textResponse = await response.text();
          console.error('❌ Non-JSON error response:', textResponse);
          throw new Error(`Server error (${response.status}): ${textResponse || response.statusText}`);
        }

        console.error('❌ Backend error response:', errorData);
        console.error('❌ Error details:', JSON.stringify(errorData, null, 2));

        const errorMessage = typeof errorData.error === 'string'
          ? errorData.error
          : errorData.error?.message || errorData.message || JSON.stringify(errorData.error) || `API Error ${response.status}`;

        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error sending campaign:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send campaign'
      };
    }
  },

  /**
   * Get all campaigns
   */
  async getCampaigns(filters?: {
    status?: string;
    campaignType?: CampaignType;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ campaigns: BroadcastCampaign[]; total: number; pagination: any }>> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.campaignType) queryParams.append('campaignType', filters.campaignType);
      if (filters?.page) queryParams.append('page', String(filters.page));
      if (filters?.limit) queryParams.append('limit', String(filters.limit));

      const url = `/api/v1/admin/broadcasts/campaigns${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(`${getApiBaseUrl()}${url}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching campaigns:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch campaigns'
      };
    }
  },

  /**
   * Get campaign statistics
   */
  async getCampaignStats(campaignId: string): Promise<ApiResponse<CampaignStats>> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/broadcasts/campaigns/${campaignId}/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching campaign stats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch campaign stats'
      };
    }
  },
};
